import type { OrderIssue } from '../types/orderIssues';

const ISSUE_TYPE_LABELS: Record<string, string> = {
  label_generation_failed: 'Label Failed',
  address_validation_error: 'Address Error',
  postnl_api_error: 'PostNL Error',
  shopify_fulfillment_error: 'Shopify Error',
  incomplete_address: 'Incomplete Address',
  postal_code_invalid: 'Postal Code Invalid',
  other: 'Other',
};

const SOURCE_LABELS: Record<string, string> = {
  shipping_labels_manual: 'Manual Label',
  worker_terminal: 'Worker Terminal',
  finish_order_modal: 'Finish Order',
  bulk_finish_orders: 'Bulk Finish',
  postnl_label_modal: 'PostNL Modal',
  unknown: 'Unknown',
};

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function exportOrderIssuesToCSV(issues: OrderIssue[]): void {
  const headers = [
    'Order Reference',
    'Customer Name',
    'Issue Type',
    'Error Description',
    'Error Code',
    'Source',
    'Status',
    'Street',
    'Postal Code',
    'City',
    'Country',
    'Phone',
    'Attempts',
    'Created At',
    'Last Attempt',
  ];

  const rows = issues.map((issue) => {
    const addr = issue.recipient_address || {};
    return [
      issue.order_reference,
      issue.client_name || '',
      ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type,
      issue.issue_description,
      issue.error_code || '',
      SOURCE_LABELS[issue.source] || issue.source,
      issue.status,
      addr.street || addr.address1 || '',
      addr.postalCode || '',
      addr.city || '',
      addr.countryCode || '',
      addr.phone || '',
      String(issue.attempt_count),
      formatDate(issue.created_at),
      formatDate(issue.last_attempt_at),
    ].map((v) => escapeCSV(String(v)));
  });

  const csv = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `order-issues-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function exportOrderIssuesToExcel(issues: OrderIssue[]): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Order Issues');

  const columns = [
    { header: 'Order Reference', key: 'order_reference', width: 18 },
    { header: 'Customer Name', key: 'client_name', width: 22 },
    { header: 'Issue Type', key: 'issue_type', width: 20 },
    { header: 'Error Description', key: 'issue_description', width: 40 },
    { header: 'Error Code', key: 'error_code', width: 18 },
    { header: 'Source', key: 'source', width: 16 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Street', key: 'street', width: 24 },
    { header: 'Postal Code', key: 'postal_code', width: 14 },
    { header: 'City', key: 'city', width: 16 },
    { header: 'Country', key: 'country', width: 10 },
    { header: 'Phone', key: 'phone', width: 16 },
    { header: 'Attempts', key: 'attempts', width: 10 },
    { header: 'Created At', key: 'created_at', width: 20 },
    { header: 'Last Attempt', key: 'last_attempt', width: 20 },
  ];

  sheet.columns = columns;

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD97706' },
  };
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

  for (const issue of issues) {
    const addr = issue.recipient_address || {};
    sheet.addRow({
      order_reference: issue.order_reference,
      client_name: issue.client_name || '',
      issue_type: ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type,
      issue_description: issue.issue_description,
      error_code: issue.error_code || '',
      source: SOURCE_LABELS[issue.source] || issue.source,
      status: issue.status,
      street: addr.street || addr.address1 || '',
      postal_code: addr.postalCode || '',
      city: addr.city || '',
      country: addr.countryCode || '',
      phone: addr.phone || '',
      attempts: issue.attempt_count,
      created_at: formatDate(issue.created_at),
      last_attempt: formatDate(issue.last_attempt_at),
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `order-issues-${new Date().toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
